import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Dashboard.module.css';
import { getAuthToken } from '../../lib/auth';
import { HOST } from '../../config/api';
import { getExerciseCategoryLabel } from '../../utils/exerciseCategoryLabels';

const AthleteDashboard = ({ userId, userInfo, demoData }) => {
    const [nextTraining, setNextTraining] = useState(null);
    const [history, setHistory] = useState([]);
    const [hasCoachSubscription, setHasCoachSubscription] = useState(false);

    useEffect(() => {
        if (demoData) {
            setNextTraining(demoData.nextTraining || null);
            setHistory(demoData.history || []);
            setHasCoachSubscription(Boolean(demoData.hasCoachSubscription));
            return;
        }

        let active = true;

        const loadDashboard = async () => {
            try {
                const [trainingResponse, historyResponse, subscriptionResponse] = await Promise.all([
                    fetch(`${HOST}/v1/training?userid=${userId}`, {
                        headers: { Authorization: `Bearer ${getAuthToken()}` },
                    }),
                    fetch(`${HOST}/v1/training_history?userid=${userId}`, {
                        headers: { Authorization: `Bearer ${getAuthToken()}` },
                    }),
                    fetch(`${HOST}/v1/user_coach_subscription?userid=${userId}`, {
                        headers: { Authorization: `Bearer ${getAuthToken()}` },
                    }),
                ]);

                const trainingJson = await trainingResponse.json();
                const historyJson = await historyResponse.json();
                const subscriptionJson = await subscriptionResponse.json();

                if (!active) {
                    return;
                }

                setNextTraining(trainingJson?.lastTraining || null);
                setHistory(historyJson?.training || []);
                setHasCoachSubscription(subscriptionJson?.subCount > 0);
            } catch (error) {
                console.error('Failed to load athlete dashboard', error);
            }
        };

        loadDashboard();

        return () => {
            active = false;
        };
    }, [demoData, userId]);

    const recentHistory = history.slice(0, 3);
    const totalSubscriptions = userInfo?.coachSubscription?.length || 0;
    const recentCompletedExercises = useMemo(
        () =>
            history.reduce(
                (acc, training) => acc + training.exerciseHistoryModels.filter((exercise) => exercise.completed === 1).length,
                0
            ),
        [history]
    );

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div>
                    <span className={styles.kicker}>Athlete summary</span>
                    <h2>{`Hi ${userInfo?.firstName || 'there'}, here’s what matters today.`}</h2>
                    <p>
                        Jump straight into the next training, keep subscriptions visible, and review recent progress without
                        scrolling through long history tables first.
                    </p>
                </div>

                <div className={styles.heroActions}>
                    <Link className={styles.primaryAction} to="/trainings">
                        Open next training
                    </Link>
                    <Link className={styles.secondaryAction} to="/coaches">
                        Browse coaches
                    </Link>
                </div>
            </section>

            <section className={styles.statGrid}>
                <article className={styles.statCard}>
                    <span>Coach subscriptions</span>
                    <strong>{totalSubscriptions}</strong>
                    <small>Plans you can train against right now</small>
                </article>
                <article className={styles.statCard}>
                    <span>Completed trainings</span>
                    <strong>{userInfo?.completedTrainingsCount || 0}</strong>
                    <small>Recorded completions so far</small>
                </article>
                <article className={styles.statCard}>
                    <span>Completed exercises</span>
                    <strong>{recentCompletedExercises}</strong>
                    <small>Across your recent history</small>
                </article>
            </section>

            <section className={styles.grid}>
                <article className={`${styles.card} ${styles.cardLarge}`}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h3>Today&apos;s training</h3>
                            <p>The summary page should tell the athlete what to do next immediately.</p>
                        </div>
                        <Link className={styles.inlineLink} to="/trainings">
                            Open full training
                        </Link>
                    </div>

                    {!hasCoachSubscription ? (
                        <div className={styles.emptyState}>
                            Subscribe to a training plan to get started. Once you subscribe, your next training will appear here automatically.
                        </div>
                    ) : !nextTraining ? (
                        <div className={styles.emptyState}>
                            No training assigned yet. Your plan is active, but your coach hasn&apos;t assigned your next session yet.
                        </div>
                    ) : (
                        <div className={styles.nextTrainingCard}>
                            <strong>{nextTraining.name}</strong>
                            <span>{nextTraining.exercises?.length || 0} exercises ready to complete</span>
                            <div className={styles.exercisePreview}>
                                {(nextTraining.exercises || []).slice(0, 4).map((exercise, index) => (
                                    <div key={exercise.id || `${exercise.category}-${index}`} className={styles.exerciseRow}>
                                        <span>{index + 1}</span>
                                        <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                        <p>{exercise.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </article>

                <article className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h3>Subscriptions</h3>
                            <p>Your active plan controls which training shows up on your dashboard and in the Trainings tab.</p>
                        </div>
                        <Link className={styles.inlineLink} to="/coaches">
                            Browse coaches
                        </Link>
                    </div>

                    <div className={styles.list}>
                        {(userInfo?.coachSubscription || []).length === 0 ? (
                            <div className={styles.emptyState}>No subscriptions yet. Browse coaches to choose your first plan.</div>
                        ) : (
                            userInfo.coachSubscription.slice(0, 5).map((subscription) => (
                                <div key={subscription.id} className={styles.listRow}>
                                    <div>
                                        <strong>{subscription.name}</strong>
                                        <span>{`${subscription.first_name} ${subscription.last_name}`}</span>
                                    </div>
                                    {userInfo.activeVariant === subscription.variant_id && (
                                        <span className={styles.stateBadge}>Active</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </article>

                <article className={`${styles.card} ${styles.cardLarge}`}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h3>Recent progress</h3>
                            <p>Keep recent outcomes scannable instead of burying them in a long table.</p>
                        </div>
                    </div>

                    <div className={styles.activityTable}>
                        {recentHistory.length === 0 ? (
                            <div className={styles.emptyState}>No completed training history yet.</div>
                        ) : (
                            recentHistory.map((training, index) => {
                                const completed = training.exerciseHistoryModels.filter((exercise) => exercise.completed === 1).length;
                                const total = training.exerciseHistoryModels.length;

                                return (
                                    <div key={`${training.timestamp}-${index}`} className={styles.activityRow}>
                                        <div>
                                            <strong>{training.name}</strong>
                                            <span>{new Date(training.timestamp).toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <strong>{`${completed}/${total} completed`}</strong>
                                            <span>Review in training history</span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </article>
            </section>
        </div>
    );
};

export default AthleteDashboard;
